import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import {
  MessageCircle, Bell, Smartphone, Mail, Send, CheckCircle,
  Eye, MousePointerClick, XCircle, Euro, TrendingUp, Loader2,
  ArrowUpRight, ArrowDownRight, BarChart3,
} from 'lucide-react';

const CH_META = {
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: '#25D366', metrics: ['sent', 'delivered', 'read', 'failed'] },
  push: { label: 'Push', icon: Bell, color: '#6366F1', metrics: ['sent', 'delivered', 'clicked', 'failed'] },
  sms: { label: 'SMS', icon: Smartphone, color: '#F59E0B', metrics: ['sent', 'delivered', 'failed'] },
  email: { label: 'Email', icon: Mail, color: '#3B82F6', metrics: ['sent', 'delivered', 'read', 'clicked', 'failed'] },
};

const METRIC_LABELS = {
  sent: { label: 'Inviati', icon: Send, color: '#6366F1' },
  delivered: { label: 'Consegnati', icon: CheckCircle, color: '#10B981' },
  read: { label: 'Letti', icon: Eye, color: '#3B82F6' },
  clicked: { label: 'Click', icon: MousePointerClick, color: '#8B5CF6' },
  failed: { label: 'Falliti', icon: XCircle, color: '#EF4444' },
};

const PERIOD_OPTIONS = [
  { value: 7, label: '7 giorni' },
  { value: 14, label: '14 giorni' },
  { value: 30, label: '30 giorni' },
  { value: 90, label: '90 giorni' },
];

export function ChannelsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/analytics/channels?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-[#D44E20]" size={24} />
    </div>
  );

  if (!data) return <p className="text-center text-[#64748B] py-10">Errore caricamento dati</p>;

  const hasData = data.grand_total > 0;

  return (
    <div className="space-y-6" data-testid="channels-analytics">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A1A1A]">Rapporto Canali</h2>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-0.5">
          {PERIOD_OPTIONS.map(p => (
            <button key={p.value} onClick={() => setDays(p.value)} data-testid={`period-${p.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${days === p.value ? 'bg-white shadow-sm text-[#1A1A1A]' : 'text-[#64748B] hover:text-[#64748B]'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Send} label="Totale inviati" value={data.grand_total} color="#6366F1" />
        <StatCard icon={CheckCircle} label="Consegnati" value={data.grand_delivered}
          sub={data.grand_total > 0 ? `${data.delivery_rate}%` : '—'} color="#10B981" />
        <StatCard icon={Euro} label="Costo totale" value={`€${data.grand_cost.toFixed(2)}`} color="#F59E0B" />
        <StatCard icon={Bell} label="Iscritti Push" value={data.push_subscribers} color="#6366F1" />
      </div>

      {!hasData && (
        <div className="bg-gray-50 rounded-2xl p-8 text-center" data-testid="no-data">
          <BarChart3 className="mx-auto mb-3 text-[#ccc]" size={40} />
          <p className="text-[#64748B] text-sm">Nessun messaggio inviato negli ultimi {days} giorni</p>
          <p className="text-[#aaa] text-xs mt-1">I dati appariranno dopo il primo invio di un broadcast o automazione</p>
        </div>
      )}

      {hasData && (
        <>
          {/* Channel cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(CH_META).map(([chId, meta]) => {
              const stats = data.channels[chId];
              if (!stats) return (
                <ChannelCardEmpty key={chId} meta={meta} chId={chId} />
              );
              return <ChannelCard key={chId} chId={chId} meta={meta} stats={stats} />;
            })}
          </div>

          {/* Type breakdown */}
          {data.by_type && Object.keys(data.by_type).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-4">Breakdown per tipo</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(data.by_type).map(([type, channels]) => (
                  <div key={type} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-[#1A1A1A] mb-2 capitalize">{type === 'broadcast' ? 'Broadcast' : 'Automazioni'}</p>
                    <div className="space-y-1.5">
                      {Object.entries(channels).map(([ch, info]) => {
                        const m = CH_META[ch];
                        if (!m) return null;
                        const Icon = m.icon;
                        return (
                          <div key={ch} className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-xs" style={{ color: m.color }}>
                              <Icon size={12} /> {m.label}
                            </span>
                            <span className="text-xs font-bold text-[#1A1A1A]">{info.count} msg — €{info.cost.toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily chart (simple bar visualization) */}
          {data.daily && data.daily.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-4">Andamento giornaliero</h3>
              <DailyChart daily={data.daily} />
            </div>
          )}

          {/* Recent messages */}
          {data.recent && data.recent.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-[#1A1A1A] mb-4">Ultimi messaggi</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="recent-messages-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Canale</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Tipo</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Template</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Cliente</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Stato</th>
                      <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Costo</th>
                      <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((msg, i) => {
                      const m = CH_META[msg.channel] || {};
                      const Icon = m.icon || Send;
                      return (
                        <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/50">
                          <td className="py-2 px-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium"
                              style={{ backgroundColor: `${m.color || '#888'}12`, color: m.color || '#888' }}>
                              <Icon size={10} /> {m.label || msg.channel}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-[#64748B] capitalize">{msg.type}</td>
                          <td className="py-2 px-2 text-[#1A1A1A] font-medium truncate max-w-[120px]">{msg.template_name || '—'}</td>
                          <td className="py-2 px-2 text-[#64748B] truncate max-w-[100px]">{msg.customer_name || '—'}</td>
                          <td className="py-2 px-2"><StatusBadge status={msg.status} /></td>
                          <td className="py-2 px-2 text-right font-mono text-[#1A1A1A]">€{(msg.cost || 0).toFixed(4)}</td>
                          <td className="py-2 px-2 text-right text-[#64748B]">{formatDate(msg.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon size={16} style={{ color }} />
        </div>
      </div>
      <p className="text-xl font-bold text-[#1A1A1A]">{value}</p>
      <p className="text-[10px] text-[#64748B] mt-0.5">{label}</p>
      {sub && <p className="text-[10px] font-bold mt-0.5" style={{ color }}>{sub}</p>}
    </div>
  );
}

function ChannelCard({ chId, meta, stats }) {
  const Icon = meta.icon;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={`channel-card-${chId}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}12` }}>
            <Icon size={20} style={{ color: meta.color }} />
          </div>
          <div>
            <p className="text-sm font-bold text-[#1A1A1A]">{meta.label}</p>
            <p className="text-[10px] text-[#64748B]">{stats.total} messaggi totali</p>
          </div>
        </div>
        <span className="text-lg font-bold" style={{ color: meta.color }}>€{stats.total_cost.toFixed(2)}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {meta.metrics.map(metric => {
          const ml = METRIC_LABELS[metric];
          if (!ml) return null;
          const MIcon = ml.icon;
          const val = stats[metric] || 0;
          let rate = '';
          if (metric === 'delivered' && stats.delivery_rate > 0) rate = `${stats.delivery_rate}%`;
          if (metric === 'read' && stats.read_rate > 0) rate = `${stats.read_rate}%`;
          if (metric === 'clicked' && stats.click_rate > 0) rate = `${stats.click_rate}%`;
          return (
            <div key={metric} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <MIcon size={12} style={{ color: ml.color }} />
                <span className="text-[10px] font-medium text-[#64748B]">{ml.label}</span>
              </div>
              <p className="text-base font-bold text-[#1A1A1A]">{val}</p>
              {rate && <p className="text-[10px] font-bold" style={{ color: ml.color }}>{rate}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChannelCardEmpty({ chId, meta }) {
  const Icon = meta.icon;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm opacity-50" data-testid={`channel-card-${chId}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}08` }}>
          <Icon size={20} style={{ color: `${meta.color}66` }} />
        </div>
        <div>
          <p className="text-sm font-medium text-[#aaa]">{meta.label}</p>
          <p className="text-[10px] text-[#ccc]">Nessun messaggio inviato</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    sent: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Inviato' },
    delivered: { bg: 'bg-green-50', text: 'text-green-700', label: 'Consegnato' },
    read: { bg: 'bg-indigo-50', text: 'text-indigo-700', label: 'Letto' },
    clicked: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Click' },
    failed: { bg: 'bg-red-50', text: 'text-red-700', label: 'Fallito' },
    queued: { bg: 'bg-gray-50', text: 'text-gray-700', label: 'In coda' },
  };
  const s = styles[status] || styles.sent;
  return <span className={`${s.bg} ${s.text} px-2 py-0.5 rounded-md text-[10px] font-bold`}>{s.label}</span>;
}

function DailyChart({ daily }) {
  // Group by date, then show stacked bars per channel
  const byDate = {};
  daily.forEach(d => {
    if (!byDate[d.date]) byDate[d.date] = {};
    byDate[d.date][d.channel] = (byDate[d.date][d.channel] || 0) + d.count;
  });
  const dates = Object.keys(byDate).sort();
  const maxCount = Math.max(...dates.map(d => Object.values(byDate[d]).reduce((s, v) => s + v, 0)), 1);

  return (
    <div className="flex items-end gap-1 h-32 overflow-x-auto" data-testid="daily-chart">
      {dates.map(date => {
        const total = Object.values(byDate[date]).reduce((s, v) => s + v, 0);
        const pct = Math.max(4, (total / maxCount) * 100);
        return (
          <div key={date} className="flex flex-col items-center min-w-[24px] flex-shrink-0 group relative">
            <div className="w-5 rounded-t-md overflow-hidden flex flex-col-reverse" style={{ height: `${pct}%` }}>
              {Object.entries(byDate[date]).map(([ch, count]) => {
                const chPct = (count / total) * 100;
                const color = CH_META[ch]?.color || '#888';
                return <div key={ch} style={{ height: `${chPct}%`, backgroundColor: color, minHeight: '2px' }} />;
              })}
            </div>
            <span className="text-[8px] text-[#aaa] mt-1 -rotate-45 origin-left whitespace-nowrap">
              {date.slice(5)}
            </span>
            <div className="absolute bottom-full mb-1 bg-[#111114] text-white text-[9px] px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {date}: {total} msg
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return '—'; }
}
